import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  ElementRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormArray,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResult } from '../../../../shared/models/api-result.model';
import {
  Product,
  ProductAttribute,
  ProductImage,
  ProductStatus,
} from '../../../models/product.model';
import { ProductService } from '../../../services/product.service';
import { BrandService } from '../../../services/brand.service';
import { CategoryService } from '../../../services/category.service';
import { ModelService } from '../../../services/model.service';
import { ModelVersionService } from '../../../services/model-version.service';
import { VariationAttributeService } from '../../../services/variation-attribute.service';
import { VariationAttributeValueService } from '../../../services/variation-attribute-value.service';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import {
  DragDropModule,
  CdkDragDrop,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { DropdownOption } from '../../../../shared/models/dropdowm.model';
import { QuillModule } from 'ngx-quill';
import { ToolbarModule } from 'primeng/toolbar';
import { uniqueValueValidator } from '../validators/variation-attributeId.validator';
import { Toast, ToastModule } from 'primeng/toast';

interface ProductImageItem {
  id?: string;
  file?: File;
  previewUrl: string;
  sortOrder: number;
  imageFileId?: string;
}

@Component({
  selector: 'app-product-create-or-update',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ToggleSwitchModule,
    ProgressSpinnerModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
    DragDropModule,
    QuillModule,
    ToolbarModule,
    ToastModule,
  ],
  templateUrl: './product-create-or-update.component.html',
  styles: `
    :host ::ng-deep .description-editor .ql-editor {
      min-height: 400px;
      max-height: 600px;
      overflow-y: auto;
      width: 100%;
    }
  `,
})
export class ProductCreateOrUpdateComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private formGroupService = inject(FormGroupService);
  private productService = inject(ProductService);
  private brandService = inject(BrandService);
  private categoryService = inject(CategoryService);
  private modelService = inject(ModelService);
  private modelVersionService = inject(ModelVersionService);
  private variationAttributeService = inject(VariationAttributeService);
  private variationAttributeValueService = inject(
    VariationAttributeValueService,
  );

  @ViewChild('htmlArea') htmlArea?: ElementRef<HTMLTextAreaElement>;

  private readonly maxImageSizeBytes = 5 * 1024 * 1024; // 5MB

  formGroup: FormGroup = this.fb.group({
    id: [null],
    name: [null, [Validators.required, Validators.maxLength(255)]],
    description: [null, Validators.required],
    sku: [null, [Validators.required, Validators.maxLength(50)]],
    brandId: [null, [Validators.required]],
    categoryId: [null, [Validators.required]],
    modelId: [{ value: null, disabled: true }, [Validators.required]],
    modelVersionId: [{ value: null, disabled: true }, [Validators.required]],
    status: [ProductStatus.Draft, [Validators.required]],
    attributes: this.fb.array([], {
      validators: [uniqueValueValidator('variationAttributeId')],
    }),
    images: [null, [Validators.required, Validators.min(4), Validators.max(8)]],
  });

  productId = signal<string | null>(null);
  loading = signal(false);

  brandsDropdown = signal<DropdownOption[]>([]);
  categoriesDropdown = signal<DropdownOption[]>([]);
  modelsDropdown = signal<DropdownOption[]>([]);
  modelVersionsDropdown = signal<DropdownOption[]>([]);

  brandSelectLoading = signal(false);
  categorySelectLoading = signal(false);
  modelSelectLoading = signal(false);
  modelVersionSelectLoading = signal(false);
  variationAttributeSelectLoading = signal(false);

  variationAttributesDropdown = signal<DropdownOption[]>([]);
  variationAttributeValuesDropdown = signal<DropdownOption[][]>([]);
  variationAttributeValuesLoading = signal<boolean[]>([]);

  productImages = signal<ProductImageItem[]>([]);

  quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean'],
    ],
    clipboard: { matchVisual: false },
  };

  quillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'align',
    'list',
    'indent',
    'blockquote',
    'code-block',
    'link',
    'image',
    'video',
  ];

  get variationAttributesArray(): FormArray {
    return this.formGroup.get('attributes') as FormArray;
  }

  statusOptions = this.productService.getProductStatusOptions();

  htmlMode = signal(false);
  htmlText = signal('');

  ngOnInit(): void {
    this.loadBrandsDropdown();
    this.loadCategoryDropdown();
    this.loadVariationAttributeDropdown();

    this.activatedRoute.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.productId.set(id);
        this.loadData(id);
      }
    });
  }

  ngOnDestroy(): void {
    this.productImages().forEach((img) => {
      if (img.file && img.previewUrl) {
        URL.revokeObjectURL(img.previewUrl);
      }
    });
  }

  getVariationAttributeControl(index: number, controlName: string) {
    return (this.variationAttributesArray.at(index) as FormGroup).get(
      controlName,
    );
  }

  onBrandChange(value: any): void {
    const brandId = value ?? this.formGroup.get('brandId')?.value ?? null;
    const categoryId = this.formGroup.get('categoryId')?.value ?? null;

    this.formGroup.patchValue({
      brandId,
      modelId: null,
      modelVersionId: null,
    });

    this.modelsDropdown.set([]);
    this.modelVersionsDropdown.set([]);

    if (!brandId || !categoryId) return;

    this.loadModels(brandId.value, categoryId.value, false);
  }

  onCategoryChange(value: any): void {
    const categoryId = value ?? this.formGroup.get('categoryId')?.value ?? null;
    const brandId = this.formGroup.get('brandId')?.value ?? null;

    this.formGroup.patchValue({
      categoryId,
      modelId: null,
      modelVersionId: null,
    });

    this.modelsDropdown.set([]);
    this.modelVersionsDropdown.set([]);

    this.formGroup.get('modelId')?.disable();
    if (!this.productId()) {
      this.formGroup.get('modelVersionId')?.disable();
    }

    if (!brandId || !categoryId) return;

    this.loadModels(brandId.value, categoryId.value, false);
  }

  onModelChange(value: any): void {
    const modelId = value ?? this.formGroup.get('modelId')?.value ?? null;

    this.formGroup.patchValue({
      modelId,
      modelVersionId: null,
    });

    this.modelVersionsDropdown.set([]);
    if (!this.productId()) {
      this.formGroup.get('modelVersionId')?.disable();
    }

    if (!modelId) return;

    this.loadModelVersions(modelId.value, false);
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const existing = this.productImages();
    let nextOrder =
      existing.length > 0
        ? Math.max(...existing.map((i) => i.sortOrder)) + 1
        : 0;

    const newItems: ProductImageItem[] = [];

    Array.from(input.files).forEach((file) => {
      if (!file.type!.startsWith('image/')) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: `O arquivo '${file.name}' deve ser uma imagem (ex: jpg, png, webp, etc.).`,
          life: 5000,
        });
        return;
      }

      if (file.size > this.maxImageSizeBytes) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: `O arquivo '${file.name}' deve ter no máximo 5MB.`,
          life: 5000,
        });
        return;
      }

      newItems.push({
        file,
        previewUrl: URL.createObjectURL(file),
        sortOrder: nextOrder++,
      });
    });

    this.productImages.set([...existing, ...newItems]);

    input.value = '';

    this.syncImagesControl(true);
  }

  onImageDrop(event: CdkDragDrop<ProductImageItem[]>): void {
    const images = [...this.productImages()];
    moveItemInArray(images, event.previousIndex, event.currentIndex);

    images.forEach((img, index) => (img.sortOrder = index));
    this.productImages.set(images);

    this.syncImagesControl(false);
  }

  onVariationAttributeChange(index: number, value: any): void {
    const group = this.variationAttributesArray.at(index) as FormGroup;

    group.patchValue({
      variationAttributeId: value,
      variationAttributeValueId: null,
    });

    const current = this.variationAttributeValuesDropdown();
    current[index] = [];
    this.variationAttributeValuesDropdown.set([...current]);

    const loading = this.variationAttributeValuesLoading();
    loading[index] = true;
    this.variationAttributeValuesLoading.set([...loading]);

    if (!value) return;

    this.loadVariationAttributeValues(index, value.value, false);
  }

  removeImage(index: number): void {
    const images = [...this.productImages()];
    const [removed] = images.splice(index, 1);

    if (removed && removed.file && removed.previewUrl) {
      URL.revokeObjectURL(removed.previewUrl);
    }

    images.forEach((img, i) => (img.sortOrder = i));
    this.productImages.set(images);

    this.syncImagesControl(true);
  }

  addVariationAttribute(): void {
    const group = this.fb.group({
      variationAttributeId: [null, [Validators.required]],
      variationAttributeValueId: [null, [Validators.required]],
    });

    this.variationAttributesArray.push(group);

    const current = this.variationAttributeValuesDropdown();
    current.push([]);
    this.variationAttributeValuesDropdown.set([...current]);
  }

  removeVariationAttribute(index: number): void {
    this.variationAttributesArray.removeAt(index);

    const current = this.variationAttributeValuesDropdown();
    current.splice(index, 1);
    this.variationAttributeValuesDropdown.set([...current]);
  }

  onSubmit(): void {
    this.syncImagesControl(true);

    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }
    this.loading.set(true);

    const formData = this.buildFormData();
    let request$: Observable<ApiResult>;

    if (this.productId()) {
      request$ = this.productService.update(this.productId()!, formData);
    } else {
      request$ = this.productService.create(formData);
    }

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Produto salvo com sucesso',
          life: 2000,
        });

        this.router.navigate(['/products']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors?.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors?.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup,
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors?.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar o produto',
              life: 3000,
            });
          }
        }
      },
    });
  }

  toggleHtmlMode(): void {
    const ctrl = this.formGroup.get('description');
    if (!ctrl) return;

    const next = !this.htmlMode();
    this.htmlMode.set(next);

    if (next) {
      // entering HTML mode
      this.htmlText.set((ctrl.value ?? '') as string);

      // focus textarea next tick
      queueMicrotask(() => this.htmlArea?.nativeElement.focus());
    } else {
      // leaving HTML mode -> write back to Quill/form
      ctrl.setValue(this.htmlText());
      ctrl.markAsDirty();
      ctrl.markAsTouched();
    }
  }

  onHtmlInput(ev: Event): void {
    this.htmlText.set((ev.target as HTMLTextAreaElement).value);
  }

  private loadBrandsDropdown(): void {
    this.brandSelectLoading.set(true);

    this.brandService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        this.brandSelectLoading.set(false);

        const options = response.data as DropdownOption[];
        this.brandsDropdown.set(options);
      },
      error: () => {
        this.brandSelectLoading.set(false);

        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: 'Ocorreu um erro ao carregar as marcas',
          life: 3000,
        });
      },
    });
  }

  private loadCategoryDropdown(): void {
    this.categorySelectLoading.set(true);

    this.categoryService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        this.categorySelectLoading.set(false);

        const options = response.data as DropdownOption[];
        this.categoriesDropdown.set(options);
      },
      error: () => {
        this.categorySelectLoading.set(false);

        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: 'Ocorreu um erro ao carregar as categorias',
          life: 3000,
        });
      },
    });
  }

  private loadVariationAttributeDropdown(): void {
    this.variationAttributeSelectLoading.set(true);

    this.variationAttributeService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        this.variationAttributeSelectLoading.set(false);

        const options = response.data as DropdownOption[];
        this.variationAttributesDropdown.set(options);
      },
      error: () => {
        this.variationAttributeSelectLoading.set(false);

        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: 'Ocorreu um erro ao carregar os atributos de variação',
          life: 3000,
        });
      },
    });
  }

  private loadModels(
    brandId: string,
    categoryId: string,
    fromLoad: boolean,
    model: DropdownOption | null = null,
  ): void {
    this.modelSelectLoading.set(true);

    this.modelService
      .getDropdownByBrandAndCategory(brandId, categoryId)
      .subscribe({
        next: (response: ApiResult) => {
          this.modelSelectLoading.set(false);

          const options = response.data as DropdownOption[];
          this.modelsDropdown.set(options);

          if (fromLoad && model) this.formGroup.get('modelId')?.setValue(model);

          this.formGroup.get('modelId')?.enable();
        },
        error: () => {
          this.modelSelectLoading.set(false);

          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar os modelos',
            life: 3000,
          });
        },
      });
  }

  private loadModelVersions(
    modelId: string,
    fromLoad: boolean,
    modelVersion: DropdownOption | null = null,
  ): void {
    this.modelVersionSelectLoading.set(true);

    this.modelVersionService.getDropdownByModel(modelId).subscribe({
      next: (response: ApiResult) => {
        this.modelVersionSelectLoading.set(false);

        const options = response.data as DropdownOption[];
        this.modelVersionsDropdown.set(options);

        if (fromLoad && modelVersion?.value)
          this.formGroup.get('modelVersionId')?.setValue(modelVersion);

        this.formGroup.get('modelVersionId')?.enable();
      },
      error: () => {
        this.modelVersionSelectLoading.set(false);

        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: 'Ocorreu um erro ao carregar as versões do modelo',
          life: 3000,
        });
      },
    });
  }

  private loadData(id: string): void {
    this.loading.set(true);

    this.productService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const product = response.data as Product;
        this.formGroup.patchValue(product);
        this.formGroup.get('status')?.setValue(ProductStatus[product.status]);

        this.formGroup.get('brandId')?.setValue({
          label: product.brandName,
          value: product.brandId,
        });

        this.formGroup.get('categoryId')?.setValue({
          label: product.categoryName,
          value: product.categoryId,
        });

        if (product.brandId && product.categoryId) {
          const model = {
            label: product.modelName,
            value: product.modelId,
          } as DropdownOption;

          const modelVersion = {
            label: product.modelVersionName,
            value: product.modelVersionId,
          } as DropdownOption;

          this.loadModels(product.brandId, product.categoryId, true, model);

          this.loadModelVersions(model.value, true, modelVersion);
        }

        if (Array.isArray(product.images)) {
          const sorted = [...product.images].sort(
            (a, b) => a.sortOrder - b.sortOrder,
          );

          this.productImages.set(
            sorted.map(
              (img: ProductImage) =>
                ({
                  id: img.id,
                  previewUrl: img.imageUrl,
                  sortOrder: img.sortOrder,
                  imageFileId: img.imageFileId,
                }) as ProductImageItem,
            ),
          );

          this.syncImagesControl(false);
        }

        const variationAttrs =
          (product.attributes as ProductAttribute[] | undefined) ?? [];

        if (variationAttrs.length > 0) {
          variationAttrs.forEach((va, index) => {
            const group = this.fb.group({
              variationAttributeId: [
                {
                  label: va.variationAttributeName,
                  value: va.variationAttributeId,
                },
                [Validators.required],
              ],
              variationAttributeValueId: [
                {
                  label: va.variationAttributeValue,
                  value: va.variationAttributeValueId,
                },
                [Validators.required],
              ],
            });

            this.variationAttributesArray.push(group);

            const current = this.variationAttributeValuesDropdown();
            current[index] = [];
            this.variationAttributeValuesDropdown.set([...current]);

            const variationAttributeValue = {
              label: va.variationAttributeValue,
              value: va.variationAttributeValueId,
            };

            this.loadVariationAttributeValues(
              index,
              va.variationAttributeId,
              true,
              variationAttributeValue,
            );
          });
        }
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar o produto',
            life: 3000,
          });
        }
      },
    });
  }

  private loadVariationAttributeValues(
    index: number,
    variationAttributeId: string,
    fromLoad: boolean,
    variationAttributeValue: DropdownOption | null = null,
  ): void {
    this.variationAttributeValueService
      .getDropdownByAttribute(variationAttributeId)
      .subscribe({
        next: (response: ApiResult) => {
          const options = response.data as DropdownOption[];
          const current = this.variationAttributeValuesDropdown();
          current[index] = options;
          this.variationAttributeValuesDropdown.set([...current]);

          const loading = this.variationAttributeValuesLoading();
          loading[index] = false;
          this.variationAttributeValuesLoading.set([...loading]);

          if (fromLoad && variationAttributeValue?.value) {
            const group = this.variationAttributesArray.at(index) as FormGroup;
            group.patchValue(variationAttributeValue);
          }
        },
        error: () => {
          const loading = this.variationAttributeValuesLoading();
          loading[index] = false;
          this.variationAttributeValuesLoading.set([...loading]);

          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar os valores de atributo',
            life: 3000,
          });
        },
      });
  }

  private buildFormData(): FormData {
    const data = this.formGroup.getRawValue();
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
      if (key === 'variationAttributes') return;
      if (['brandId', 'categoryId', 'modelId'].includes(key)) return;

      const value = (data as any)[key];
      if (value !== null && value !== undefined) {
        if (value.label && value.label)
          formData.append(key, String(value.value));
        else formData.append(key, String(value));
      }
    });

    const images = this.productImages();
    images.forEach((img, index) => {
      if (img.id) {
        formData.append(`images[${index}].id`, img.id);
      }
      if (img.imageFileId) {
        formData.append(`images[${index}].imageFileId`, img.imageFileId);
      }
      formData.append(`images[${index}].sortOrder`, img.sortOrder.toString());

      if (img.file) {
        formData.append(`images[${index}].image`, img.file);
      }
    });

    const variationAttrs = this.variationAttributesArray.value as {
      variationAttributeId: DropdownOption;
      variationAttributeValueId: DropdownOption;
    }[];

    variationAttrs.forEach((va, index) => {
      formData.append(
        `attributes[${index}].variationAttributeId`,
        String(va.variationAttributeId.value),
      );
      formData.append(
        `attributes[${index}].variationAttributeValueId`,
        String(va.variationAttributeValueId.value),
      );
    });

    formData.delete('images');
    formData.delete('attributes');

    return formData;
  }

  private syncImagesControl(markTouched = false): void {
    const control = this.formGroup.get('images');
    if (!control) return;

    const count = this.productImages().length;
    control.setValue(count > 0 ? count : null, { emitEvent: false });

    if (markTouched) control.markAsTouched();
    control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }
}
